import { useState } from "react";
import type React from "react";
import {
	domains,
	practiceCatalog,
	situations,
	type DomainId,
	type Practice,
	type PracticeId,
	type ScoreMap,
	type Situation,
	type SituationId,
} from "@/pages/AssessmentPlayground";

type TabKey = "today" | "practice" | "growth" | "profile";
type ScreenKey =
	| "today"
	| "question"
	| "recommend"
	| "tutorial"
	| "breathing"
	| "checkin"
	| "record"
	| "practice"
	| "library"
	| "growth"
	| "profile"
	| "goodThings"
	| "drawResult"
	| "quiz"
	| "quizResult"
	| "controlCircle";

const css = `
@import url("https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@500;600&family=Inter:wght@400;500;600&family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,400..600,0..1,0");

.pp-root {
  --surface-primary: #FAF2E9;
  --surface-card: #FFFCF7;
  --surface-sunken: #F3E9DC;
  --foreground-primary: #35291F;
  --foreground-secondary: #8A7A6B;
  --foreground-muted: #B7AB9C;
  --foreground-inverse: #FFFFFF;
  --accent-primary: #E0906C;
  --accent-primary-deep: #B25E3D;
  --accent-primary-soft: #F8E4D8;
  --accent-secondary: #E9B468;
  --accent-secondary-soft: #FAEAD0;
  --border-soft: #EEE2D3;
  --perma-p: #EFAE4A;
  --perma-p-soft: #FBE8C6;
  --perma-e: #E8895C;
  --perma-e-soft: #FBE2D7;
  --perma-r: #E58AA6;
  --perma-r-soft: #FAE0E9;
  --perma-m: #A98BD0;
  --perma-m-soft: #EADFF8;
  --perma-a: #7FA8C9;
  --perma-a-soft: #DCE8F3;
  width: 100vw;
  height: 100dvh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E7E1D8;
  color: var(--foreground-primary);
  font-family: Inter, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

.pp-root *,
.pp-root *::before,
.pp-root *::after {
  box-sizing: border-box;
}

.pp-root :where(button, input, textarea) {
  font: inherit;
}

.pp-root :where(button) {
  border: 0;
  padding: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.pp-phone {
  width: min(100vw, 390px);
  height: min(100dvh, 844px);
  min-height: min(100dvh, 844px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--surface-primary);
  box-shadow: 0 24px 80px rgba(53, 41, 31, 0.16);
}

@media (max-width: 430px) {
  .pp-phone {
    width: 100vw;
    height: 100dvh;
    min-height: 100dvh;
    box-shadow: none;
  }
}

.pp-status {
  height: 62px;
  flex: 0 0 62px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  background: transparent;
}

.pp-time {
  color: var(--foreground-primary);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}

.pp-indicators {
  display: flex;
  align-items: center;
  gap: 7px;
}

.pp-icon {
  display: inline-flex;
  width: var(--icon-size, 20px);
  height: var(--icon-size, 20px);
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: "Material Symbols Rounded";
  font-size: var(--icon-size, 20px);
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-feature-settings: "liga";
  -webkit-font-smoothing: antialiased;
  font-variation-settings:
    "FILL" var(--icon-fill, 0),
    "wght" var(--icon-weight, 500),
    "GRAD" 0,
    "opsz" 24;
}

.pp-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.pp-content::-webkit-scrollbar,
.pp-row-scroll::-webkit-scrollbar {
  display: none;
}

.pp-title,
.pp-heading,
.pp-button,
.pp-card-title {
  font-family: "Funnel Sans", Inter, "PingFang SC", sans-serif;
}

.pp-primary {
  width: 100%;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  border-radius: 14px;
  background: var(--accent-primary);
  color: var(--foreground-inverse);
  font-family: "Funnel Sans", Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  transition: transform 420ms ease, opacity 420ms ease, background 420ms ease;
}

.pp-primary:active,
.pp-soft-button:active,
.pp-card-button:active,
.pp-option:active,
.pp-check-row:active {
  transform: scale(0.985);
}

.pp-primary:disabled {
  cursor: not-allowed;
  background: var(--surface-sunken);
  color: var(--foreground-muted);
  transform: none;
}

.pp-footer {
  flex: 0 0 auto;
}

.pp-tabbar {
  width: calc(100% - 32px);
  max-width: 358px;
  height: 72px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto max(12px, env(safe-area-inset-bottom));
  padding: 8px;
  border: 1px solid var(--border-soft);
  border-radius: 28px;
  background: rgba(255, 252, 247, 0.92);
  box-shadow:
    0 14px 36px rgba(53, 41, 31, 0.12),
    0 2px 8px rgba(53, 41, 31, 0.06);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.pp-tab {
  flex: 1 1 0;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 5px 0 4px;
  border-radius: 22px;
  color: var(--foreground-muted);
  font-size: 11px;
  font-weight: 500;
  transition:
    background 260ms ease,
    color 260ms ease,
    transform 260ms ease;
}

.pp-tab:active {
  transform: scale(0.96);
}

.pp-tab-active {
  color: var(--accent-primary-deep);
  background: var(--accent-primary-soft);
  font-weight: 600;
}

.pp-tab .pp-icon {
  width: 28px;
  height: 28px;
  overflow: visible;
  font-size: 25px;
  line-height: 1;
}

.pp-tab span {
  line-height: 1.1;
}

.pp-header-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pp-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 999px;
}

.pp-card {
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
}

.pp-card-active {
  border-color: var(--accent-primary);
  background: var(--accent-primary-soft);
}

.pp-back-header {
  height: 46px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 12px;
}

.pp-back-title {
  flex: 1;
  color: var(--foreground-primary);
  font-family: "Funnel Sans", Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
}

.pp-kicker {
  color: var(--foreground-muted);
  font-size: 12px;
  font-weight: 500;
}

.pp-row-scroll {
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.pp-input-like {
  width: 100%;
  border: 0;
  outline: 0;
  resize: none;
  background: transparent;
  color: var(--foreground-primary);
}

.today-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 16px 8px;
}

.today-grid {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.today-row {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  gap: 11px;
}

.area-card {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  padding: 15px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  text-align: left;
  transition: transform 360ms ease, border-color 360ms ease, background 360ms ease;
}

.area-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
}

.area-check {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: var(--accent-primary);
  color: var(--foreground-inverse);
}

.question-content {
  display: flex;
  flex-direction: column;
  padding: 0;
}

.question-header {
  height: 46px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
}

.question-hint {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 12px;
  color: var(--foreground-secondary);
  font-size: 13px;
  font-weight: 500;
}

.question-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 20px 20px;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
}

.question-option,
.quiz-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  transition: transform 360ms ease, border-color 360ms ease, background 360ms ease;
}

.radio-dot {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: grid;
  place-items: center;
  border: 1.5px solid var(--foreground-muted);
  border-radius: 999px;
  color: var(--foreground-primary);
}

.radio-active {
  border-color: transparent;
  background: var(--accent-primary);
}

.recommend-content {
  display: flex;
  flex-direction: column;
}

.recommend-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 6px 16px 20px;
}

.heard-card {
  display: grid;
  gap: 6px;
  padding: 16px;
}

.practice-card {
  display: grid;
  gap: 12px;
  padding: 18px;
  border-radius: 16px;
  background: var(--accent-primary);
}

.start-in-card,
.draw-button {
  min-height: 47px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 13px 18px;
  border-radius: 14px;
  background: var(--surface-card);
  color: var(--accent-primary-deep);
  font-size: 15px;
  font-weight: 600;
}

.why-card {
  display: flex;
  gap: 9px;
  padding: 13px;
  border-radius: 16px;
  background: var(--accent-secondary-soft);
}

.lighter-card {
  min-height: 47px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 13px 18px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  color: var(--foreground-secondary);
  font-size: 14px;
  font-weight: 500;
}

.save-only {
  width: 100%;
  color: var(--foreground-muted);
  font-size: 13px;
  font-weight: 500;
  text-align: center;
}

.tutorial-content,
.checkin-content,
.library-content,
.quiz-result-content,
.control-content,
.good-content {
  display: flex;
  flex-direction: column;
}

.record-content {
  display: block;
}

.tutorial-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 16px 16px;
}

.tutorial-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px;
  border-radius: 16px;
  background: var(--accent-primary-soft);
}

.tutorial-ring,
.action-ring {
  display: grid;
  place-items: center;
  border-radius: 999px;
}

.tutorial-ring {
  width: 88px;
  height: 88px;
  background: var(--accent-primary);
}

.tutorial-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
}

.step-num {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--accent-primary-soft);
  color: var(--accent-primary-deep);
  font-size: 13px;
  font-weight: 600;
}

.breathing-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.breath-top,
.draw-top,
.quiz-top {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
}

.breath-hero {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  padding: 20px;
}

.breath-outer,
.breath-mid,
.breath-inner {
  display: grid;
  place-items: center;
  border-radius: 999px;
}

.breath-outer {
  width: 248px;
  height: 248px;
  background: var(--accent-primary-soft);
}

.breath-mid {
  width: 196px;
  height: 196px;
  background: #E0906C40;
}

.breath-inner {
  width: 148px;
  height: 148px;
  background: var(--accent-primary);
}

.breath-phases {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breath-controls {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0 24px 28px;
}

.pause-button {
  width: 100%;
  min-height: 49px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 14px 20px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  color: var(--foreground-secondary);
  font-size: 15px;
  font-weight: 600;
}

.checkin-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 12px 20px 28px;
}

.action-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
}

.action-ring {
  width: 72px;
  height: 72px;
  background: var(--accent-primary-soft);
}

.pp-check-row {
  min-height: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 20px;
  border-radius: 16px;
  background: var(--accent-primary);
  color: var(--foreground-primary);
  font-size: 17px;
  font-weight: 600;
}

.checkin-note {
  width: 100%;
  min-height: 76px;
  resize: none;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  color: var(--foreground-primary);
  padding: 15px 16px;
  font-size: 14px;
  line-height: 1.55;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  transition:
    border-color 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease;
}

.checkin-note::placeholder {
  color: var(--foreground-muted);
}

.checkin-note:focus {
  border-color: var(--accent-primary);
  background: #fffdfa;
  box-shadow: 0 0 0 4px rgba(224, 144, 108, 0.14);
}

.check-white {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--surface-card);
}

.record-main {
  min-height: auto;
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 6px 16px 96px;
}

.done-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px;
  border-radius: 16px;
  background: var(--accent-primary-soft);
}

.done-check {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--accent-primary);
}

.record-field {
  display: grid;
  gap: 7px;
  padding: 14px;
}

.record-textarea {
  width: 100%;
  min-height: 56px;
  resize: none;
  border: 0;
  background: transparent;
  color: var(--foreground-primary);
  padding: 0;
  font-size: 14px;
  line-height: 1.45;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.record-textarea::placeholder {
  color: var(--foreground-muted);
}

.photo-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 14px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  color: var(--foreground-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.photo-button input {
  display: none;
}

.photo-preview {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
}

.photo-preview img {
  display: block;
  width: 100%;
  max-height: 168px;
  object-fit: cover;
}

.photo-preview-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  color: var(--foreground-secondary);
  font-size: 12px;
}

.photo-remove {
  flex: 0 0 auto;
  color: var(--accent-primary-deep);
  font-size: 12px;
  font-weight: 600;
}

.sync-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  text-align: left;
}

.switch {
  position: relative;
  width: 44px;
  height: 26px;
  flex: 0 0 44px;
  border-radius: 999px;
  background: var(--surface-sunken);
  transition: background 300ms ease;
}

.switch-on {
  background: var(--accent-primary);
}

.knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: var(--surface-card);
  transition: transform 300ms ease;
}

.switch-on .knob {
  transform: translateX(18px);
}

.growth-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 6px 16px 16px;
}

.summary-card {
  display: grid;
  gap: 10px;
  padding: 14px;
}

.summary-strip {
  display: flex;
  gap: 5px;
}

.summary-strip span {
  flex: 1 1 0;
  height: 18px;
  border-radius: 5px;
}

.journal-day {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.journal-card {
  display: grid;
  gap: 9px;
  padding: 14px;
}

.journal-dot {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 999px;
}

.journal-tag,
.journal-done {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--surface-sunken);
  color: var(--foreground-secondary);
  font-size: 10px;
  font-weight: 600;
}

.journal-tag span {
  width: 6px;
  height: 6px;
  border-radius: 999px;
}

.journal-done {
  gap: 3px;
  background: var(--accent-primary-soft);
  color: var(--accent-primary-deep);
}

.practice-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 8px 16px 16px;
}

.draw-card {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 16px;
  background: var(--accent-primary);
}

.dim-row {
  display: flex;
  gap: 10px;
  width: max-content;
}

.dim-card {
  width: 104px;
  display: grid;
  gap: 10px;
  padding: 13px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  text-align: left;
}

.dim-dot {
  width: 11px;
  height: 11px;
  border-radius: 999px;
}

.dim-track {
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--surface-sunken);
}

.dim-track span {
  display: block;
  height: 6px;
  border-radius: 999px;
}

.quiz-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px;
  border-radius: 16px;
  background: var(--accent-secondary-soft);
  text-align: left;
}

.quiz-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: var(--accent-secondary);
}

.library-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 20px 28px;
}

.filter-row {
  display: flex;
  gap: 8px;
  width: max-content;
}

.filter-chip {
  padding: 9px 15px;
  border: 1px solid var(--border-soft);
  border-radius: 999px;
  background: var(--surface-card);
  color: var(--foreground-secondary);
  font-size: 14px;
  font-weight: 500;
}

.filter-active {
  border-color: var(--accent-primary);
  background: var(--accent-primary);
  color: var(--foreground-primary);
  font-weight: 600;
}

.library-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.library-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
  text-align: left;
}

.library-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: var(--accent-primary-soft);
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 26px;
  padding: 8px 20px 16px;
}

.profile-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
}

.avatar {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 18px;
}

.profile-group {
  display: grid;
  gap: 8px;
}

.goals-card {
  display: grid;
  gap: 12px;
  padding: 16px;
}

.goal-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 13px;
  border-radius: 999px;
  background: var(--accent-primary-soft);
  color: var(--accent-primary-deep);
  font-size: 13px;
  font-weight: 600;
}

.profile-list {
  overflow: hidden;
}

.profile-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 16px;
  text-align: left;
}

.profile-row-border {
  border-top: 1px solid var(--border-soft);
}

.profile-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: var(--surface-sunken);
  color: var(--foreground-secondary);
}

.profile-icon-accent {
  background: var(--accent-primary-soft);
  color: var(--accent-primary-deep);
}

.good-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 12px;
}

.good-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 20px 28px;
}

.good-slot {
  display: flex;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--surface-card);
}

.good-active {
  border-color: var(--accent-primary);
}

.good-check,
.good-num {
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.good-check {
  background: var(--accent-primary);
}

.good-num {
  border: 1px solid var(--foreground-muted);
  color: var(--foreground-muted);
}

.draw-result-content {
  display: flex;
  flex-direction: column;
}

.draw-result-hero {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  padding: 20px;
}

.draw-result-card {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid var(--perma-p);
  border-radius: 18px;
  background: var(--perma-p-soft);
}

.quiz-content {
  display: flex;
  flex-direction: column;
}

.quiz-track {
  flex: 1;
  height: 6px;
  overflow: hidden;
  margin: 0 14px;
  border-radius: 999px;
  background: var(--surface-sunken);
}

.quiz-track span {
  display: block;
  width: 78px;
  height: 6px;
  border-radius: 999px;
  background: var(--accent-primary);
}

.quiz-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px 20px;
}

.quiz-scale {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
}

.quiz-result-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 6px 16px 20px;
}

.score-card {
  display: grid;
  justify-items: center;
  gap: 6px;
  padding: 22px;
  border-radius: 16px;
  background: var(--accent-primary);
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bar-track {
  flex: 1;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--surface-sunken);
}

.bar-track span {
  display: block;
  height: 8px;
  border-radius: 999px;
}

.control-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 20px 28px;
}

.control-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 16px;
}

.control-item,
.control-add {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  background: var(--surface-card);
  color: var(--foreground-primary);
  font-size: 14px;
}

.control-add {
  justify-content: center;
  background: transparent;
  color: var(--foreground-muted);
  font-size: 13px;
  font-weight: 500;
}
`;

const areas = [
	{
		id: "sleep",
		label: "睡不好",
		icon: "bedtime",
		soft: "var(--perma-m-soft)",
		color: "var(--perma-m)",
	},
	{
		id: "stress",
		label: "压力 / 焦虑",
		icon: "cyclone",
		soft: "var(--accent-primary-soft)",
		color: "var(--accent-primary-deep)",
	},
	{
		id: "action",
		label: "拖延 / 没劲",
		icon: "hourglass_top",
		soft: "var(--perma-a-soft)",
		color: "var(--perma-a)",
	},
	{
		id: "low",
		label: "情绪低落",
		icon: "rainy",
		soft: "var(--perma-e-soft)",
		color: "var(--perma-e)",
	},
	{
		id: "relationship",
		label: "相处很累",
		icon: "group",
		soft: "var(--perma-r-soft)",
		color: "var(--perma-r)",
	},
	{
		id: "body",
		label: "身体疲惫",
		icon: "battery_2_bar",
		soft: "var(--accent-secondary-soft)",
		color: "var(--accent-secondary)",
	},
	{
		id: "order",
		label: "生活乱 / 手机",
		icon: "smartphone",
		soft: "var(--perma-p-soft)",
		color: "var(--perma-p)",
	},
	{
		id: "positive",
		label: "想积极一点",
		icon: "celebration",
		soft: "var(--accent-secondary-soft)",
		color: "var(--accent-secondary)",
	},
] as const;

type QuestionGroup = {
	title: string;
	hint: string;
	options: readonly ProductQuestionOption[];
};

type ProductQuestionOption = {
	label: string;
	domainScores?: ScoreMap<DomainId>;
	situationScores?: ScoreMap<SituationId>;
};

const questionGroups: Record<string, QuestionGroup> = {
	sleep: {
		title: "睡不好，更像哪一种？",
		hint: "睡眠问题背后可能是身体没降下来，也可能是节律被打乱。",
		options: [
			{
				label: "入睡很久，脑子停不下来",
				domainScores: { sleep: 1, stress: 1 },
				situationScores: { sleep_onset: 2, sleep_rumination: 2, stress_anxiety: 1 },
			},
			{
				label: "半夜容易醒，醒后难睡回去",
				domainScores: { sleep: 1 },
				situationScores: { sleep_nightwake: 3 },
			},
			{
				label: "睡够了但醒来还是累",
				domainScores: { sleep: 1, body: 1 },
				situationScores: { sleep_tired: 3, body_fatigue: 1 },
			},
			{
				label: "作息乱，睡觉起床时间漂移",
				domainScores: { sleep: 1, order: 1 },
				situationScores: { sleep_rhythm: 3, order_chaos: 1 },
			},
			{
				label: "睡前刷手机停不下来",
				domainScores: { sleep: 1, order: 1 },
				situationScores: { sleep_phone: 3, phone_overuse: 1 },
			},
		],
	},
	stress: {
		title: "压力或情绪，最像哪一种？",
		hint: "同样是压力，背后的小练习会完全不同。",
		options: [
			{
				label: "反复担心未来或结果",
				domainScores: { stress: 1 },
				situationScores: { stress_anxiety: 3 },
			},
			{
				label: "事情太多，脑子很满",
				domainScores: { stress: 1, action: 1 },
				situationScores: { stress_overload: 3 },
			},
			{
				label: "容易烦躁，想发火",
				domainScores: { stress: 1 },
				situationScores: { stress_irritable: 3 },
			},
			{
				label: "低落，没什么开心感",
				domainScores: { stress: 1, positive: 1 },
				situationScores: { low_mood: 3, positive_low_joy: 1 },
			},
			{
				label: "心跳、胸闷、强烈害怕",
				domainScores: { stress: 1, body: 1 },
				situationScores: { panic_signal: 3 },
			},
		],
	},
	action: {
		title: "拖延或没劲，更像哪一种？",
		hint: "启动困难时，先判断是入口太大、能量太低，还是害怕做不好。",
		options: [
			{
				label: "知道要做，但就是开始不了",
				domainScores: { action: 1 },
				situationScores: { action_procrastination: 3 },
			},
			{
				label: "任务太大，不知道第一步",
				domainScores: { action: 1, stress: 1 },
				situationScores: { action_unclear_step: 3, stress_overload: 1 },
			},
			{
				label: "没动力，提不起劲",
				domainScores: { action: 1, body: 1 },
				situationScores: { action_low_motivation: 3, body_fatigue: 1 },
			},
			{
				label: "怕做不好，所以一直拖",
				domainScores: { action: 1, self: 1 },
				situationScores: { perfection_block: 3, self_criticism: 1 },
			},
			{
				label: "手机或杂事把我带走",
				domainScores: { action: 1, order: 1 },
				situationScores: { phone_overuse: 2, action_procrastination: 2 },
			},
		],
	},
	low: {
		title: "情绪低落，更像哪一种？",
		hint: "低落时不急着变好，先找到最轻的一步。",
		options: [
			{
				label: "今天没什么开心感",
				domainScores: { stress: 1, positive: 1 },
				situationScores: { positive_low_joy: 3, low_mood: 1 },
			},
			{
				label: "什么都不想做，只想躺着",
				domainScores: { stress: 1, action: 1 },
				situationScores: { low_mood: 2, action_low_motivation: 2 },
			},
			{
				label: "容易自责，觉得自己不够好",
				domainScores: { self: 2 },
				situationScores: { self_criticism: 3, low_mood: 1 },
			},
			{
				label: "对事情没期待",
				domainScores: { positive: 1, stress: 1 },
				situationScores: { positive_low_joy: 2, low_mood: 2 },
			},
			{
				label: "有点孤单但不想麻烦别人",
				domainScores: { relationship: 1, stress: 1 },
				situationScores: { relationship_lonely: 2, low_mood: 1 },
			},
		],
	},
	relationship: {
		title: "相处很累，最像哪一种？",
		hint: "关系练习不一定要深聊，先找到一个低压力连接或边界动作。",
		options: [
			{
				label: "想联系别人，但不知道怎么开口",
				domainScores: { relationship: 1 },
				situationScores: { relationship_lonely: 3, self_low_confidence: 1 },
			},
			{
				label: "和别人有点冲突或别扭",
				domainScores: { relationship: 1, stress: 1 },
				situationScores: { relationship_conflict: 3, stress_irritable: 1 },
			},
			{
				label: "想表达边界，但怕伤关系",
				domainScores: { relationship: 1, self: 1 },
				situationScores: { relationship_conflict: 2, self_low_confidence: 1 },
			},
			{
				label: "需要被支持，但说不出口",
				domainScores: { relationship: 1, stress: 1 },
				situationScores: { relationship_lonely: 2, stress_overload: 1 },
			},
			{
				label: "不想社交但又觉得孤单",
				domainScores: { relationship: 1, stress: 1 },
				situationScores: { relationship_lonely: 2, low_mood: 1 },
			},
		],
	},
	body: {
		title: "身体疲惫，更像哪一种？",
		hint: "身体状态会影响情绪，先看是疲惫、紧绷，还是基础照顾断了。",
		options: [
			{
				label: "身体很累，像没电",
				domainScores: { body: 2 },
				situationScores: { body_fatigue: 3 },
			},
			{
				label: "肩颈、胸口或胃很紧",
				domainScores: { body: 2 },
				situationScores: { body_tension: 3, stress_anxiety: 1 },
			},
			{
				label: "很久没喝水、吃饭或动一动",
				domainScores: { body: 2 },
				situationScores: { body_fatigue: 2, action_low_motivation: 1 },
			},
			{
				label: "坐着不舒服但也动不起来",
				domainScores: { body: 1, action: 1 },
				situationScores: { body_tension: 1, body_fatigue: 1, action_low_motivation: 1 },
			},
			{
				label: "身体不是主要问题",
				domainScores: { body: -1, stress: 1 },
				situationScores: { stress_overload: 1 },
			},
		],
	},
	order: {
		title: "生活乱或手机，最像哪一种？",
		hint: "生活乱通常不是意志力问题，先找一个环境或节律入口。",
		options: [
			{
				label: "手机停不下来",
				domainScores: { order: 2 },
				situationScores: { phone_overuse: 3 },
			},
			{
				label: "空间很乱，心也跟着乱",
				domainScores: { order: 2 },
				situationScores: { order_chaos: 3 },
			},
			{
				label: "计划太多，不知道先做什么",
				domainScores: { order: 1, stress: 1 },
				situationScores: { order_chaos: 2, stress_overload: 1 },
			},
			{
				label: "作息乱，节奏漂了",
				domainScores: { sleep: 1, order: 1 },
				situationScores: { sleep_rhythm: 2, order_chaos: 1 },
			},
			{
				label: "事情堆着，看见就烦",
				domainScores: { stress: 1, order: 1 },
				situationScores: { stress_overload: 2, order_chaos: 1 },
			},
		],
	},
	positive: {
		title: "想积极一点，先从哪里开始？",
		hint: "积极不是硬装开心，而是让大脑重新看见真实存在的资源。",
		options: [
			{
				label: "想看见今天好的部分",
				domainScores: { positive: 2 },
				situationScores: { positive_low_joy: 2 },
			},
			{
				label: "想多一点期待感",
				domainScores: { positive: 1, action: 1 },
				situationScores: { positive_low_joy: 2, action_low_motivation: 1 },
			},
			{
				label: "想练感恩，但不知道写什么",
				domainScores: { positive: 2 },
				situationScores: { positive_low_joy: 2 },
			},
			{
				label: "想记录一个值得记住的时刻",
				domainScores: { positive: 2 },
				situationScores: { positive_low_joy: 1 },
			},
			{
				label: "只是想轻松一点",
				domainScores: { positive: 1, stress: 1 },
				situationScores: { stress_overload: 1, positive_low_joy: 1 },
			},
		],
	},
};

type ProductScoreSource = {
	domainScores?: ScoreMap<DomainId>;
	situationScores?: ScoreMap<SituationId>;
};

type ProductRecommendation = {
	alternatives: PracticeId[];
	domainName: string;
	icon: string;
	practice: Practice;
	practiceId: PracticeId;
	score: number;
	secondarySituation?: Situation;
	situation: Situation;
	situationId: SituationId;
	statusLabel: string;
};

const entryScoreMap: Record<string, ProductScoreSource> = {
	sleep: { domainScores: { sleep: 2 } },
	stress: {
		domainScores: { stress: 2 },
		situationScores: { stress_anxiety: 1, stress_overload: 1 },
	},
	action: {
		domainScores: { action: 2 },
		situationScores: { action_procrastination: 1, action_low_motivation: 1 },
	},
	low: {
		domainScores: { stress: 1, positive: 1 },
		situationScores: { low_mood: 2, positive_low_joy: 1 },
	},
	relationship: {
		domainScores: { relationship: 2 },
		situationScores: { relationship_conflict: 1, relationship_lonely: 1 },
	},
	body: {
		domainScores: { body: 2 },
		situationScores: { body_fatigue: 2, sleep_tired: 1 },
	},
	order: {
		domainScores: { order: 2 },
		situationScores: { order_chaos: 1, phone_overuse: 2, sleep_phone: 1 },
	},
	positive: {
		domainScores: { positive: 2 },
		situationScores: { positive_low_joy: 2 },
	},
};

const fallbackSituationByArea: Record<string, SituationId> = {
	sleep: "sleep_onset",
	stress: "stress_anxiety",
	action: "action_procrastination",
	low: "low_mood",
	relationship: "relationship_lonely",
	body: "body_fatigue",
	order: "phone_overuse",
	positive: "positive_low_joy",
};

const practiceIconMap: Partial<Record<PracticeId, string>> = {
	"sleep-breath": "bedtime",
	"body-scan": "radio_button_checked",
	"worry-dump": "edit_note",
	"tomorrow-first-step": "flag",
	"lights-down": "light_mode",
	"fixed-wake": "alarm",
	"phone-away": "smartphone",
	"white-noise": "graphic_eq",
	"box-breath": "air",
	"grounding-54321": "filter_5",
	"emotion-name": "sentiment_satisfied",
	"control-circle": "adjust",
	"one-minute-start": "bolt",
	"single-priority": "task_alt",
	"done-list": "checklist",
	"self-kind-sentence": "favorite",
	"message-one": "forum",
	"boundary-sentence": "chat",
	"short-walk": "directions_walk",
	water: "water_drop",
	"tidy-five": "inventory_2",
	"gratitude-one": "local_florist",
	"safety-contact": "support_agent",
	"professional-help": "health_and_safety",
};

const modeIconMap: Record<Practice["mode"], string> = {
	认知: "lightbulb",
	身体: "self_improvement",
	行动: "bolt",
	关系: "forum",
	积极: "local_florist",
	记录: "edit_note",
	安全: "health_and_safety",
};

function buildRecommendationFromAnswers(
	selectedAreas: string[],
	selectedQuestion: string | null,
	alternativeOffset = 0,
): ProductRecommendation {
	const domainScores = createZeroScores(Object.keys(domains) as DomainId[]);
	const situationScores = createZeroScores(Object.keys(situations) as SituationId[]);
	const activeArea = selectedAreas[selectedAreas.length - 1] ?? "stress";
	const activeGroup = questionGroups[activeArea] ?? questionGroups.stress;
	const selectedOption = activeGroup.options.find((option) => option.label === selectedQuestion);

	for (const area of selectedAreas) {
		addScores(domainScores, entryScoreMap[area]?.domainScores);
		addScores(situationScores, entryScoreMap[area]?.situationScores);
	}

	addScores(domainScores, selectedOption?.domainScores);
	addScores(situationScores, selectedOption?.situationScores);

	const rankedSituations = sortProductSituationScores(situationScores).filter((item) => item.score > 0);
	const topSituationId =
		rankedSituations[0]?.id ?? fallbackSituationByArea[activeArea] ?? "stress_overload";
	const situation = situations[topSituationId];
	const practiceIds: PracticeId[] =
		situation.practiceIds.length > 0 ? situation.practiceIds : ["box-breath"];
	const practiceId =
		practiceIds[alternativeOffset % practiceIds.length] ?? practiceIds[0] ?? "box-breath";
	const practice = practiceCatalog[practiceId];
	const secondary = rankedSituations.find((item) => item.id !== topSituationId && item.score > 0);

	return {
		alternatives: practiceIds.filter((id) => id !== practiceId),
		domainName: domains[situation.domain].name,
		icon: practiceIconMap[practiceId] ?? modeIconMap[practice.mode],
		practice,
		practiceId,
		score: rankedSituations[0]?.score ?? 0,
		secondarySituation: secondary ? situations[secondary.id] : undefined,
		situation,
		situationId: topSituationId,
		statusLabel: `${situation.name} · ${practice.category}`,
	};
}

function createZeroScores<T extends string>(keys: readonly T[]) {
	return keys.reduce(
		(acc, key) => {
			acc[key] = 0;
			return acc;
		},
		{} as Record<T, number>,
	);
}

function addScores<T extends string>(target: Record<T, number>, scores?: ScoreMap<T>) {
	if (!scores) {
		return;
	}

	for (const [id, score] of Object.entries(scores) as Array<[T, number | undefined]>) {
		target[id] = Math.max(0, (target[id] ?? 0) + (score ?? 0));
	}
}

function sortProductSituationScores(scores: Record<SituationId, number>) {
	return Object.entries(scores)
		.map(([id, score]) => ({ id: id as SituationId, score: Number(score) }))
		.sort((a, b) => {
			if (a.id === "safety_risk" && a.score >= 3) {
				return -1;
			}
			if (b.id === "safety_risk" && b.score >= 3) {
				return 1;
			}
			return b.score - a.score;
		});
}

const libraryGroups = [
	{
		name: "身体调节",
		mode: "身体",
		icon: "self_improvement",
		count: 7,
		items: [
			["方块呼吸", "2 分钟 · 焦虑时先降唤醒", "self_improvement"],
			["5-4-3-2-1 稳定", "3 分钟 · 强情绪 / 惊恐", "self_improvement"],
			["快速身体扫描", "2-4 分钟 · 身体紧绷 / 入睡", "self_improvement"],
			["睡前延长呼气", "2 分钟 · 入睡难", "self_improvement"],
			["短距离走一圈", "3-5 分钟 · 低动力", "self_improvement"],
			["白噪音放松", "5-20 分钟 · 浅睡 / 环境干扰", "self_improvement"],
			["喝水", "30 秒 · 极低能量", "self_improvement"],
		],
	},
	{
		name: "认知整理",
		mode: "认知",
		icon: "lightbulb",
		count: 3,
		items: [
			["可控 / 不可控分类", "3 分钟 · 焦虑担心", "lightbulb"],
			["写下所有担忧", "3 分钟 · 睡前想太多", "lightbulb"],
			["给自己写一句温和的话", "1 分钟 · 自责内耗", "lightbulb"],
		],
	},
	{
		name: "行动启动",
		mode: "行动",
		icon: "bolt",
		count: 7,
		items: [
			["只做 1 分钟", "1 分钟 · 拖延 / 怕做不好", "bolt"],
			["写下今天唯一重点", "1 分钟 · 压力过载", "bolt"],
			["写下明天第一步", "1 分钟 · 第一步不清", "bolt"],
			["整理空间 5 分钟", "5 分钟 · 生活秩序乱", "bolt"],
			["手机放到够不到的地方", "1 分钟 · 手机停不下", "bolt"],
			["设置固定起床时间", "1 分钟 · 作息乱", "bolt"],
			["睡前调暗灯光", "1 分钟 · 入睡 / 作息", "bolt"],
		],
	},
	{
		name: "关系连接",
		mode: "关系",
		icon: "forum",
		count: 2,
		items: [
			["给一个人发一条消息", "2 分钟 · 孤独断联", "forum"],
			["写一句边界表达", "3 分钟 · 关系冲突", "forum"],
		],
	},
	{
		name: "积极建构",
		mode: "积极",
		icon: "celebration",
		count: 2,
		items: [
			["列今天已完成 3 件事", "2 分钟 · 情绪低落", "celebration"],
			["写下一件感恩的事", "1 分钟 · 缺少正向体验", "celebration"],
		],
	},
	{
		name: "情绪记录",
		mode: "记录",
		icon: "edit_note",
		count: 1,
		items: [["给当前状态命名", "1 分钟 · 情绪混乱 / 说不清", "edit_note"]],
	},
	{
		name: "安全支持",
		mode: "安全",
		icon: "health_and_safety",
		count: 2,
		items: [
			["联系可信任的人", "立即 · 出现风险信号", "health_and_safety"],
			["联系专业帮助", "立即 · 强烈风险 / 长期严重", "health_and_safety"],
		],
	},
] as const;

const filters = ["全部", "身体", "认知", "行动", "关系", "积极", "记录", "安全"] as const;

const dims = [
	["积极", "Lv.2", "var(--perma-p)", 47],
	["投入", "Lv.1", "var(--perma-e)", 23],
	["关系", "Lv.2", "var(--perma-r)", 55],
	["意义", "Lv.1", "var(--perma-m)", 16],
	["成就", "Lv.3", "var(--perma-a)", 66],
] as const;

const journalEntries = [
	{
		day: "今天 · 6月13日",
		items: [
			{
				title: "三件好事",
				time: "21:40",
				tag: "积极",
				color: "var(--perma-p)",
				text: "原来好事一直在，只是今天我停下来看了：赶完方案、路上的晚霞、朋友的一条消息。",
			},
			{
				title: "方块呼吸",
				time: "15:20",
				tag: "投入",
				color: "var(--perma-e)",
				done: true,
				text: "做完松了一点，脑子没那么吵了。",
			},
		],
	},
	{
		day: "昨天 · 6月12日",
		items: [
			{
				title: "给一个人发消息",
				time: "20:18",
				tag: "关系",
				color: "var(--perma-r)",
				text: "给朋友发了句「最近还好吗」，发出去之后，关系好像没那么远了。",
			},
		],
	},
	{
		day: "6月11日 · 周三",
		items: [
			{
				title: "只做 1 分钟",
				time: "09:30",
				tag: "成就",
				color: "var(--perma-a)",
				done: true,
				text: "把拖了很久的邮件开了个头，原来开始没那么难。",
			},
		],
	},
] as const;

const tabConfig: Array<{ key: TabKey; label: string; icon: string }> = [
	{ key: "today", label: "今天", icon: "wb_sunny" },
	{ key: "practice", label: "练习", icon: "spa" },
	{ key: "growth", label: "成长", icon: "forest" },
	{ key: "profile", label: "我的", icon: "person" },
];

function cx(...parts: Array<string | false | null | undefined>) {
	return parts.filter(Boolean).join(" ");
}

function Icon({
	name,
	size = 20,
	fill = false,
	weight = 500,
	className,
	style,
}: {
	name: string;
	size?: number;
	fill?: boolean;
	weight?: number;
	className?: string;
	style?: React.CSSProperties;
}) {
	return (
		<span
			className={cx("pp-icon", className)}
			style={
				{
					"--icon-size": `${size}px`,
					"--icon-fill": fill ? 1 : 0,
					"--icon-weight": weight,
					...style,
				} as React.CSSProperties
			}
		>
			{name}
		</span>
	);
}

function PrimaryButton({
	children,
	onClick,
	disabled,
}: {
	children: React.ReactNode;
	onClick?: () => void;
	disabled?: boolean;
}) {
	return (
		<button type="button" className="pp-primary pp-button" onClick={onClick} disabled={disabled}>
			{children}
		</button>
	);
}

function TabBar({
	active,
	onChange,
}: {
	active: TabKey;
	onChange: (tab: TabKey) => void;
}) {
	return (
		<nav className="pp-tabbar" aria-label="底部导航">
			{tabConfig.map((tab) => (
				<button
					key={tab.key}
					type="button"
					className={cx("pp-tab", active === tab.key && "pp-tab-active")}
					onClick={() => onChange(tab.key)}
				>
					<Icon name={tab.icon} size={24} fill={active === tab.key} />
					<span>{tab.label}</span>
				</button>
			))}
		</nav>
	);
}

function Screen({
	children,
	footer,
	tab,
	onTab,
	className,
}: {
	children: React.ReactNode;
	footer?: React.ReactNode;
	tab?: TabKey;
	onTab: (tab: TabKey) => void;
	className?: string;
}) {
	return (
		<div className="pp-phone">
			<div className={cx("pp-content", className)}>{children}</div>
			{footer ? <div className="pp-footer">{footer}</div> : null}
			{tab ? <TabBar active={tab} onChange={onTab} /> : null}
		</div>
	);
}

function BackHeader({
	title,
	tag,
	tagIcon,
	onBack,
}: {
	title: string;
	tag?: string;
	tagIcon?: string;
	onBack: () => void;
}) {
	return (
		<div className="pp-back-header">
			<button type="button" onClick={onBack} aria-label="返回">
				<Icon name="chevron_left" size={28} />
			</button>
			<div className="pp-back-title">{title}</div>
			{tag ? (
				<div
					className="pp-chip"
					style={{
						padding: "4px 10px",
						background: "var(--accent-primary-soft)",
						color: "var(--accent-primary-deep)",
						fontSize: 11,
						fontWeight: 600,
					}}
				>
					{tagIcon ? <Icon name={tagIcon} size={12} fill /> : null}
					<span>{tag}</span>
				</div>
			) : null}
		</div>
	);
}

function TodayPage({
	selected,
	setSelected,
	next,
	onTab,
}: {
	selected: string[];
	setSelected: (value: string[]) => void;
	next: () => void;
	onTab: (tab: TabKey) => void;
}) {
	function toggle(id: string) {
		if (selected.includes(id)) {
			setSelected(selected.filter((item) => item !== id));
			return;
		}
		if (selected.length >= 3) {
			setSelected([selected[1], selected[2], id]);
			return;
		}
		setSelected([...selected, id]);
	}

	return (
		<Screen
			tab="today"
			onTab={onTab}
			className="today-content"
			footer={
				<div style={{ padding: "8px 16px 12px" }}>
					<PrimaryButton onClick={next} disabled={selected.length === 0}>
						下一步
					</PrimaryButton>
				</div>
			}
		>
			<div className="pp-header-line">
				<div style={{ display: "grid", gap: 2 }}>
					<div className="pp-title" style={{ fontSize: 26, fontWeight: 600 }}>
						晚上好
					</div>
					<div style={{ color: "var(--foreground-muted)", fontSize: 13 }}>
						6月13日 · 周五
					</div>
				</div>
				<div
					className="pp-chip"
					style={{
						background: "var(--accent-primary-soft)",
						color: "var(--accent-primary-deep)",
						padding: "7px 12px",
						gap: 5,
						fontSize: 14,
						fontWeight: 600,
					}}
				>
					<Icon name="local_fire_department" size={15} fill />
					<span>7 天</span>
				</div>
			</div>

			<div style={{ display: "grid", gap: 3 }}>
				<div className="pp-heading" style={{ fontSize: 20, fontWeight: 600 }}>
					今天，哪里最想被照顾一下？
				</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13 }}>
					选 1–3 个，我帮你找一个小练习
				</div>
			</div>

			<div className="today-grid">
				{[0, 2, 4, 6].map((start) => (
					<div className="today-row" key={start}>
						{areas.slice(start, start + 2).map((area) => {
							const active = selected.includes(area.id);
							return (
								<button
									type="button"
									key={area.id}
									className={cx("area-card", active && "pp-card-active")}
									onClick={() => toggle(area.id)}
								>
									<div className="pp-header-line" style={{ width: "100%" }}>
										<span
											className="area-icon"
											style={{ background: area.soft, color: area.color }}
										>
											<Icon name={area.icon} size={23} fill />
										</span>
										{active ? (
											<span className="area-check">
												<Icon name="check" size={14} weight={600} />
											</span>
										) : null}
									</div>
									<div
										className="pp-card-title"
										style={{
											color: active
												? "var(--accent-primary-deep)"
												: "var(--foreground-primary)",
											fontSize: 15,
											fontWeight: 600,
										}}
									>
										{area.label}
									</div>
								</button>
							);
						})}
					</div>
				))}
			</div>
		</Screen>
	);
}

function QuestionPage({
	selected,
	setSelected,
	next,
	back,
	group,
	skip,
}: {
	selected: string | null;
	setSelected: (value: string | null) => void;
	next: () => void;
	back: () => void;
	group: QuestionGroup;
	skip: () => void;
}) {
	return (
		<Screen
			onTab={() => undefined}
			className="question-content"
			footer={
				<div style={{ padding: "8px 20px 28px", display: "grid", gap: 10, justifyItems: "center" }}>
					<PrimaryButton onClick={next} disabled={!selected}>
						下一步
					</PrimaryButton>
					<button
						type="button"
						style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}
						onClick={skip}
					>
						说不清，先给我一个轻练习
					</button>
				</div>
			}
		>
			<div className="question-header">
				<button type="button" onClick={back} aria-label="返回">
					<Icon name="chevron_left" size={26} />
				</button>
				<div className="question-hint">
					<Icon name="graphic_eq" size={15} style={{ color: "var(--accent-primary-deep)" }} />
					<span>正在慢慢了解你</span>
				</div>
				<div style={{ width: 26, height: 26 }} />
			</div>

			<div className="question-main">
				<div className="pp-heading" style={{ fontSize: 24, fontWeight: 600 }}>
					{group.title}
				</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13, lineHeight: 1.45 }}>
					{group.hint}
				</div>
				<div className="question-options">
					{group.options.map((option) => {
						const active = selected === option.label;
						return (
							<button
								type="button"
								key={option.label}
								className={cx("question-option", active && "pp-card-active")}
								onClick={() => setSelected(option.label)}
							>
								<span className={cx("radio-dot", active && "radio-active")}>
									{active ? <Icon name="check" size={14} weight={600} /> : null}
								</span>
								<span
									style={{
										flex: 1,
										color: active ? "var(--accent-primary-deep)" : "var(--foreground-primary)",
										fontSize: 15,
										fontWeight: active ? 600 : 500,
										textAlign: "left",
									}}
								>
									{option.label}
								</span>
							</button>
						);
					})}
				</div>
			</div>
		</Screen>
	);
}

function RecommendPage({
	recommendation,
	start,
	back,
	lighter,
	recordOnly,
}: {
	recommendation: ProductRecommendation;
	start: () => void;
	back: () => void;
	lighter: () => void;
	recordOnly: () => void;
}) {
	const secondaryName = recommendation.secondarySituation?.name;

	return (
		<Screen onTab={() => undefined} className="recommend-content">
			<BackHeader title="为你选的练习" onBack={back} />
			<div className="recommend-main">
				<div className="pp-card heard-card">
					<div className="pp-kicker">我们大概听到的是</div>
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<div className="pp-heading" style={{ fontSize: 20, fontWeight: 600 }}>
							{recommendation.situation.name}
						</div>
						{secondaryName ? (
							<div
								className="pp-chip"
								style={{
									background: "var(--surface-sunken)",
									color: "var(--foreground-secondary)",
									padding: "3px 9px",
									fontSize: 11,
									fontWeight: 500,
								}}
							>
								也有点{secondaryName}
							</div>
						) : null}
					</div>
					<div style={{ color: "var(--foreground-secondary)", fontSize: 13, lineHeight: 1.5 }}>
						{recommendation.situation.description} 这不是诊断，只是帮你先找一个合适的小练习。
					</div>
				</div>

				<div className="practice-card">
					<div className="pp-header-line">
						<div
							className="pp-chip"
							style={{
								background: "#FFFFFF33",
								color: "var(--foreground-inverse)",
								padding: "4px 9px",
								fontSize: 11,
								fontWeight: 600,
							}}
						>
							<Icon name={modeIconMap[recommendation.practice.mode]} size={12} />
							<span>
								{recommendation.practice.mode} · {recommendation.practice.duration} · 轻打卡
							</span>
						</div>
						<Icon name={recommendation.icon} size={24} style={{ color: "#FFFFFFCC" }} />
					</div>
					<div className="pp-heading" style={{ color: "#fff", fontSize: 23, fontWeight: 600 }}>
						{recommendation.practice.title}
					</div>
					<div style={{ color: "#FFFFFFEE", fontSize: 13, lineHeight: 1.5 }}>
						{recommendation.practice.selectionReason}
					</div>
					<button type="button" className="start-in-card" onClick={start}>
						<span>开始</span>
						<Icon name="arrow_forward" size={17} />
					</button>
				</div>

				<div className="why-card">
					<Icon name="lightbulb" size={16} fill style={{ color: "var(--accent-primary-deep)" }} />
					<div style={{ flex: 1, color: "var(--foreground-secondary)", fontSize: 12, lineHeight: 1.5 }}>
						为什么先是它：{recommendation.practice.reason}
					</div>
				</div>

				<button type="button" className="lighter-card" onClick={lighter}>
					<Icon name="swap_horiz" size={17} />
					<span>换一个更轻的</span>
				</button>

				<button type="button" className="save-only" onClick={recordOnly}>
					今天先不练，只记录状态
				</button>
			</div>
		</Screen>
	);
}

function TutorialPage({
	back,
	start,
	skip,
}: {
	back: () => void;
	start: () => void;
	skip: () => void;
}) {
	const steps = [
		["1", "arrow_upward", "吸气", "4 秒"],
		["2", "pause", "屏住", "4 秒"],
		["3", "arrow_downward", "呼气", "4 秒"],
		["4", "pause", "屏住", "4 秒"],
	] as const;

	return (
		<Screen
			onTab={() => undefined}
			className="tutorial-content"
			footer={
				<div style={{ padding: "6px 16px 24px", display: "grid", gap: 8, justifyItems: "center" }}>
					<PrimaryButton onClick={start}>开始跟练</PrimaryButton>
					<button
						type="button"
						onClick={skip}
						style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}
					>
						我已经会了，直接完成
					</button>
				</div>
			}
		>
			<BackHeader title="方块呼吸" tag="身体 · 2 分钟" onBack={back} />
			<div className="tutorial-main">
				<div className="tutorial-hero">
					<div className="tutorial-ring">
						<Icon name="air" size={42} style={{ color: "#fff" }} />
					</div>
					<div className="pp-heading" style={{ fontSize: 18, fontWeight: 600 }}>
						跟着节奏，一拍 4 秒
					</div>
					<div style={{ color: "var(--foreground-secondary)", fontSize: 13, lineHeight: 1.5, textAlign: "center" }}>
						吸气 → 屏住 → 呼气 → 屏住，像走过一个方块的四条边。
					</div>
				</div>
				<div className="pp-heading" style={{ fontSize: 14, fontWeight: 600 }}>
					怎么做
				</div>
				<div className="tutorial-steps">
					{steps.map(([num, icon, label, duration]) => (
						<div className="step-row" key={num}>
							<div className="step-num">{num}</div>
							<Icon name={icon} size={18} style={{ color: "var(--foreground-secondary)" }} />
							<div style={{ flex: 1, fontSize: 15, fontWeight: 500 }}>{label}</div>
							<div style={{ color: "var(--accent-primary-deep)", fontSize: 14, fontWeight: 600 }}>
								{duration}
							</div>
						</div>
					))}
				</div>
				<div className="why-card">
					<Icon name="lightbulb" size={16} fill style={{ color: "var(--accent-primary-deep)" }} />
					<div style={{ flex: 1, color: "var(--foreground-secondary)", fontSize: 12, lineHeight: 1.5 }}>
						为什么有用：节律呼吸能帮身体从「警报」里慢下来。
					</div>
				</div>
			</div>
		</Screen>
	);
}

function BreathingPage({
	close,
	done,
}: {
	close: () => void;
	done: () => void;
}) {
	return (
		<Screen onTab={() => undefined} className="breathing-content">
			<div className="breath-top">
				<button type="button" onClick={close} aria-label="关闭">
					<Icon name="close" size={24} style={{ color: "var(--foreground-secondary)" }} />
				</button>
				<div className="pp-heading" style={{ fontSize: 16, fontWeight: 600 }}>
					方块呼吸
				</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}>
					第 2 / 4 轮
				</div>
			</div>
			<div className="breath-hero">
				<div className="breath-outer">
					<div className="breath-mid">
						<div className="breath-inner">
							<div style={{ color: "#fff", fontSize: 20, fontWeight: 600 }}>吸气</div>
							<div style={{ color: "#fff", fontSize: 44, fontWeight: 600 }}>4</div>
						</div>
					</div>
				</div>
				<div className="breath-phases">
					{["吸气", "屏住", "呼气", "屏住"].map((item, index) => (
						<div
							key={`${item}-${index}`}
							className="pp-chip"
							style={{
								padding: "5px 11px",
								background: index === 0 ? "var(--accent-primary-soft)" : "transparent",
								color: index === 0 ? "var(--accent-primary-deep)" : "var(--foreground-muted)",
								fontSize: 12,
								fontWeight: index === 0 ? 600 : 500,
							}}
						>
							{item}
						</div>
					))}
				</div>
			</div>
			<div className="breath-controls">
				<div style={{ color: "var(--foreground-muted)", fontSize: 14, fontWeight: 600 }}>01:12 / 02:00</div>
				<button type="button" className="pause-button">
					<Icon name="pause" size={18} />
					<span>暂停</span>
				</button>
				<button
					type="button"
					onClick={done}
					style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}
				>
					结束并打卡完成
				</button>
			</div>
		</Screen>
	);
}

function CheckinPage({
	back,
	next,
	recommendation,
}: {
	back: () => void;
	next: () => void;
	recommendation: ProductRecommendation;
}) {
	const [note, setNote] = useState("");

	return (
		<Screen
			onTab={() => undefined}
			className="checkin-content"
			footer={
				<div style={{ padding: "8px 20px 28px", display: "grid", gap: 10, justifyItems: "center" }}>
					<PrimaryButton onClick={next}>完成，记录今天</PrimaryButton>
					<button
						type="button"
						onClick={next}
						style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}
					>
						今天先做到这里也可以
					</button>
				</div>
			}
		>
			<BackHeader
				title={recommendation.practice.title}
				tag={recommendation.practice.mode}
				tagIcon={modeIconMap[recommendation.practice.mode]}
				onBack={back}
			/>
			<div className="checkin-main">
				<div style={{ color: "var(--foreground-secondary)", fontSize: 15, lineHeight: 1.5 }}>
					{recommendation.practice.selectionReason}
				</div>
				<div className="action-card">
					<div className="action-ring">
						<Icon name={recommendation.icon} size={34} style={{ color: "var(--accent-primary-deep)" }} />
					</div>
					<div className="pp-heading" style={{ width: "100%", textAlign: "center", fontSize: 22, fontWeight: 600 }}>
						{recommendation.practice.title}
					</div>
					<div
						className="pp-chip"
						style={{
							background: "var(--surface-sunken)",
							color: "var(--foreground-secondary)",
							padding: "8px 14px",
							fontSize: 13,
						}}
					>
						{recommendation.practice.duration} · {recommendation.practice.category}
					</div>
				</div>
				<textarea
					className="checkin-note"
					value={note}
					onChange={(event) => setNote(event.currentTarget.value)}
					placeholder="想记一句吗？比如做完后身体、情绪或行动感有什么变化。（可选）"
					aria-label="练习后记录"
					rows={3}
				/>
			</div>
		</Screen>
	);
}

function RecordPage({
	back,
	save,
	recommendation,
}: {
	back: () => void;
	save: () => void;
	recommendation: ProductRecommendation;
}) {
	const [sync, setSync] = useState(true);
	const [meaning, setMeaning] = useState("");
	const [photoName, setPhotoName] = useState("");
	const [photoPreview, setPhotoPreview] = useState("");
	const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.currentTarget.files?.[0];
		if (!file) {
			setPhotoName("");
			setPhotoPreview("");
			return;
		}

		setPhotoName(file.name);
		const reader = new FileReader();
		reader.onload = () => {
			setPhotoPreview(typeof reader.result === "string" ? reader.result : "");
		};
		reader.readAsDataURL(file);
		event.currentTarget.value = "";
	};
	const clearPhoto = () => {
		setPhotoName("");
		setPhotoPreview("");
	};

	return (
		<Screen
			onTab={() => undefined}
			className="record-content"
			footer={
				<div style={{ padding: "6px 16px 24px" }}>
					<PrimaryButton onClick={save}>存进我的成长</PrimaryButton>
				</div>
			}
		>
			<BackHeader title="记录这一次" onBack={back} />
			<div className="record-main">
				<div className="done-banner">
					<span className="done-check">
						<Icon name="check" size={18} weight={600} />
					</span>
					<div style={{ display: "grid", gap: 2, flex: 1 }}>
						<div style={{ fontSize: 14, fontWeight: 600 }}>
							完成了「{recommendation.practice.title}」
						</div>
						<div style={{ color: "var(--accent-primary-deep)", fontSize: 11 }}>
							{recommendation.practice.category} · 今天 21:34
						</div>
					</div>
				</div>
				<RecordField icon="task_alt" label="我做了什么" value={recommendation.practice.title} />
				<RecordField icon="mood" label="识别到的状态" value={recommendation.statusLabel} />
				<div className="pp-card record-field">
					<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<Icon name="favorite" size={15} style={{ color: "var(--accent-primary-deep)" }} />
						<div style={{ fontSize: 13, fontWeight: 600 }}>对我的意义</div>
					</div>
					<textarea
						className="record-textarea"
						value={meaning}
						onChange={(event) => setMeaning(event.currentTarget.value)}
						placeholder="这件小事，对你意味着什么？"
						aria-label="对我的意义"
						rows={2}
					/>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					<label className="photo-button">
						<Icon name="photo_camera" size={16} />
						<span>拍一张</span>
						<input
							type="file"
							accept="image/*"
							capture="environment"
							onChange={handlePhotoChange}
						/>
					</label>
					<div style={{ color: "var(--foreground-muted)", fontSize: 12 }}>
						{photoName ? "已添加照片" : "都可选，不写也会保存"}
					</div>
				</div>
				{photoPreview ? (
					<div className="photo-preview">
						<img src={photoPreview} alt="上传的记录照片" />
						<div className="photo-preview-meta">
							<span>{photoName}</span>
							<button type="button" className="photo-remove" onClick={clearPhoto}>
								移除
							</button>
						</div>
					</div>
				) : null}
				<button type="button" className="sync-row" onClick={() => setSync(!sync)}>
					<div style={{ display: "grid", gap: 2, flex: 1 }}>
						<div style={{ fontSize: 13, fontWeight: 600 }}>同步到共同进展墙</div>
						<div style={{ color: "var(--foreground-muted)", fontSize: 11 }}>匿名 · 不显示昵称和内容细节</div>
					</div>
					<span className={cx("switch", sync && "switch-on")}>
						<span className="knob" />
					</span>
				</button>
			</div>
		</Screen>
	);
}

function RecordField({
	icon,
	label,
	value,
	muted,
}: {
	icon: string;
	label: string;
	value: string;
	muted?: boolean;
}) {
	return (
		<div className="pp-card record-field">
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<Icon name={icon} size={15} style={{ color: "var(--accent-primary-deep)" }} />
				<div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
			</div>
			<div
				style={{
					color: muted ? "var(--foreground-muted)" : "var(--foreground-primary)",
					fontSize: 14,
					lineHeight: 1.45,
				}}
			>
				{value}
			</div>
		</div>
	);
}

function GrowthPage({
	onTab,
}: {
	onTab: (tab: TabKey) => void;
}) {
	return (
		<Screen tab="growth" onTab={onTab} className="growth-content">
			<div className="pp-header-line">
				<div className="pp-heading" style={{ fontSize: 22, fontWeight: 600 }}>
					成长
				</div>
				<div
					className="pp-chip"
					style={{
						background: "var(--accent-primary-soft)",
						color: "var(--accent-primary-deep)",
						padding: "6px 10px",
						gap: 4,
						fontSize: 13,
						fontWeight: 600,
					}}
				>
					<Icon name="local_fire_department" size={14} fill />
					连续 7 天
				</div>
			</div>
			<div className="pp-card summary-card">
				<div className="pp-header-line">
					<div style={{ color: "var(--foreground-secondary)", fontSize: 13 }}>
						已完成 26 次 · 本周 5 次
					</div>
					<div style={{ color: "var(--accent-primary-deep)", fontSize: 12, fontWeight: 600 }}>
						进展墙 →
					</div>
				</div>
				<div className="summary-strip">
					{[
						"var(--perma-p)",
						"var(--perma-a)",
						"var(--perma-r)",
						"var(--perma-e)",
						"var(--perma-a)",
						"var(--perma-r)",
						"var(--perma-p)",
						"var(--perma-m)",
						"var(--perma-a)",
						"var(--perma-r)",
						"var(--perma-p)",
						"var(--perma-e)",
						"var(--perma-a)",
						"var(--perma-r)",
					].map((color, index) => (
						<span key={index} style={{ background: color }} />
					))}
				</div>
			</div>
			{journalEntries.map((day) => (
				<div className="journal-day" key={day.day}>
					<div style={{ color: "var(--foreground-secondary)", fontSize: 13, fontWeight: 600 }}>
						{day.day}
					</div>
					{day.items.map((item) => (
						<div className="pp-card journal-card" key={item.title}>
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<span className="journal-dot" style={{ background: item.color }} />
								<div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{item.title}</div>
								<div style={{ color: "var(--foreground-muted)", fontSize: 11 }}>{item.time}</div>
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
								<span className="journal-tag">
									<span style={{ background: item.color }} />
									{item.tag}
								</span>
								{"done" in item && item.done ? (
									<span className="journal-done">
										<Icon name="check" size={11} />
										打卡完成
									</span>
								) : null}
							</div>
							<div style={{ color: "var(--foreground-secondary)", fontSize: 13, lineHeight: 1.5 }}>
								{item.text}
							</div>
						</div>
					))}
				</div>
			))}
		</Screen>
	);
}

function PracticePage({
	onTab,
	openLibrary,
	openDraw,
	openQuiz,
}: {
	onTab: (tab: TabKey) => void;
	openLibrary: () => void;
	openDraw: () => void;
	openQuiz: () => void;
}) {
	return (
		<Screen tab="practice" onTab={onTab} className="practice-content">
			<div style={{ display: "grid", gap: 2 }}>
				<div className="pp-heading" style={{ fontSize: 22, fontWeight: 600 }}>练习</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13 }}>
					不知道练什么？抽一张，或挑一个维度
				</div>
			</div>

			<div className="draw-card">
				<div className="pp-header-line">
					<div
						className="pp-chip"
						style={{ background: "#FFFFFF33", color: "#fff", padding: "5px 11px", fontSize: 11, fontWeight: 600 }}
					>
						今日抽卡
					</div>
					<div style={{ color: "#FFFFFFCC", fontSize: 12, fontWeight: 500 }}>今天还能抽 2 次</div>
				</div>
				<div className="pp-heading" style={{ color: "#fff", fontSize: 23, fontWeight: 600 }}>
					抽一张今天的练习
				</div>
				<div style={{ color: "#FFFFFFEE", fontSize: 13, lineHeight: 1.5 }}>
					随机给你一个 2–3 分钟的积极小练习，做完就点亮一块成长。
				</div>
				<button type="button" className="draw-button" onClick={openDraw}>
					<Icon name="shuffle" size={18} />
					抽一张
				</button>
			</div>

			<div className="pp-heading" style={{ fontSize: 14, fontWeight: 600 }}>按维度练 · PERMA</div>
			<div className="pp-row-scroll">
				<div className="dim-row">
					{dims.map(([name, level, color, width]) => (
						<button type="button" className="dim-card" key={name} onClick={openLibrary}>
							<div className="pp-header-line">
								<span className="dim-dot" style={{ background: color }} />
								<span style={{ color: "var(--foreground-secondary)", fontSize: 12, fontWeight: 600 }}>{level}</span>
							</div>
							<div style={{ fontSize: 15, fontWeight: 600 }}>{name}</div>
							<div className="dim-track">
								<span style={{ width, background: color }} />
							</div>
						</button>
					))}
				</div>
			</div>
			<button type="button" className="quiz-card" onClick={openQuiz}>
				<span className="quiz-icon">
					<Icon name="psychology" size={24} style={{ color: "#fff" }} />
				</span>
				<span style={{ display: "grid", gap: 3, flex: 1 }}>
					<span style={{ fontSize: 16, fontWeight: 600 }}>积极思维测评</span>
					<span style={{ color: "var(--foreground-secondary)", fontSize: 12 }}>
						12 题，看看你的积极心理水平 · 上次 72 分
					</span>
				</span>
				<Icon name="chevron_right" size={22} style={{ color: "var(--accent-primary-deep)" }} />
			</button>
		</Screen>
	);
}

function LibraryPage({
	back,
	openTutorial,
	openControl,
}: {
	back: () => void;
	openTutorial: () => void;
	openControl: () => void;
}) {
	const [filter, setFilter] = useState<(typeof filters)[number]>("全部");
	const groups = filter === "全部" ? libraryGroups : libraryGroups.filter((group) => group.mode === filter);

	return (
		<Screen onTab={() => undefined} className="library-content">
			<BackHeader title="练习库" onBack={back} />
			<div className="library-main">
				<div style={{ color: "var(--foreground-muted)", fontSize: 13, lineHeight: 1.5 }}>
					24 个小练习，都是 2–5 分钟能完成的一小步。
				</div>
				<div className="pp-row-scroll">
					<div className="filter-row">
						{filters.map((item) => (
							<button
								type="button"
								key={item}
								className={cx("filter-chip", filter === item && "filter-active")}
								onClick={() => setFilter(item)}
							>
								{item}
							</button>
						))}
					</div>
				</div>
				{groups.map((group) => (
					<div className="library-group" key={group.name}>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<Icon name={group.icon} size={18} style={{ color: "var(--accent-primary-deep)" }} />
							<div style={{ fontSize: 15, fontWeight: 600 }}>{group.name}</div>
							<div style={{ color: "var(--foreground-muted)", fontSize: 12, fontWeight: 500 }}>{group.count}</div>
						</div>
						{group.items.map(([title, meta, icon]) => (
							<button
								type="button"
								className="library-item"
								key={title}
								onClick={title === "方块呼吸" ? openTutorial : title === "可控 / 不可控分类" ? openControl : undefined}
							>
								<span className="library-icon">
									<Icon name={icon} size={19} style={{ color: "var(--accent-primary-deep)" }} />
								</span>
								<span style={{ display: "grid", gap: 3, flex: 1, minWidth: 0 }}>
									<span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
									<span style={{ color: "var(--foreground-muted)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
										{meta}
									</span>
								</span>
								<Icon name="chevron_right" size={20} style={{ color: "var(--foreground-muted)" }} />
							</button>
						))}
					</div>
				))}
			</div>
		</Screen>
	);
}

function ProfilePage({
	onTab,
}: {
	onTab: (tab: TabKey) => void;
}) {
	return (
		<Screen tab="profile" onTab={onTab} className="profile-content">
			<div className="pp-heading" style={{ fontSize: 30, fontWeight: 600 }}>我的</div>
			<div className="pp-card profile-card">
				<img src="/assets/characters/generated-1781339778363.png" alt="" className="avatar" />
				<div style={{ display: "grid", gap: 6, flex: 1 }}>
					<div style={{ fontSize: 18, fontWeight: 600 }}>正在练习的你</div>
					<div style={{ color: "var(--foreground-secondary)", fontSize: 13 }}>
						已经坚持 12 天 · 完成 28 次训练
					</div>
				</div>
			</div>
			<ProfileGoals />
			<ProfileGroup
				title="隐私与数据"
				items={[
					["lock", "隐私设置"],
					["download", "数据导出"],
					["delete", "删除记录"],
				]}
			/>
			<ProfileGroup title="提醒" items={[["notifications", "提醒设置"]]} />
			<ProfileGroup
				title="支持"
				items={[
					["favorite", "危机资源", "需要时，这里有人可以帮你", true],
					["support_agent", "专业支持入口"],
					["info", "免责声明"],
				]}
			/>
			<div style={{ color: "var(--foreground-muted)", fontSize: 12, textAlign: "center" }}>
				本产品是轻训练工具，不提供心理诊断或治疗。
			</div>
		</Screen>
	);
}

function ProfileGoals() {
	return (
		<div className="profile-group">
			<div className="pp-header-line">
				<div style={{ color: "var(--foreground-muted)", fontSize: 12, fontWeight: 600 }}>我的目标</div>
				<div style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--accent-primary-deep)", fontSize: 13, fontWeight: 600 }}>
					<Icon name="edit" size={14} />
					编辑
				</div>
			</div>
			<div className="pp-card goals-card">
				<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
					{["缓解焦虑", "提升行动感", "记录变化"].map((item) => (
						<span className="goal-chip" key={item}>
							<Icon name="check" size={14} />
							{item}
						</span>
					))}
				</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 12 }}>目标会影响推荐，随时可以增减。</div>
			</div>
		</div>
	);
}

function ProfileGroup({
	title,
	items,
}: {
	title: string;
	items: ReadonlyArray<readonly [string, string, string?, boolean?]>;
}) {
	return (
		<div className="profile-group">
			<div style={{ color: "var(--foreground-muted)", fontSize: 12, fontWeight: 600 }}>{title}</div>
			<div className="pp-card profile-list">
				{items.map(([icon, label, hint, accent], index) => (
					<button type="button" className={cx("profile-row", index > 0 && "profile-row-border")} key={label}>
						<span className={cx("profile-icon", accent && "profile-icon-accent")}>
							<Icon name={icon} size={19} fill={accent} />
						</span>
						<span style={{ display: "grid", gap: 3, flex: 1 }}>
							<span style={{ fontSize: 15, fontWeight: accent ? 600 : 500 }}>{label}</span>
							{hint ? (
								<span style={{ color: "var(--foreground-secondary)", fontSize: 12 }}>{hint}</span>
							) : null}
						</span>
						<Icon name="chevron_right" size={20} style={{ color: "var(--foreground-muted)" }} />
					</button>
				))}
			</div>
		</div>
	);
}

function GoodThingsPage({ back, save }: { back: () => void; save: () => void }) {
	return (
		<Screen
			onTab={() => undefined}
			className="good-content"
			footer={
				<div style={{ padding: "8px 20px 28px", display: "grid", gap: 10, justifyItems: "center" }}>
					<PrimaryButton onClick={save}>完成，记录今天</PrimaryButton>
					<button type="button" style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }} onClick={save}>
						想到一件也可以
					</button>
				</div>
			}
		>
			<div className="good-back">
				<button type="button" onClick={back} aria-label="返回">
					<Icon name="chevron_left" size={28} />
				</button>
				<div className="pp-heading" style={{ fontSize: 18, fontWeight: 600 }}>三件好事</div>
				<div className="pp-chip" style={{ background: "var(--accent-primary-soft)", color: "var(--accent-primary-deep)", padding: "4px 10px", fontSize: 11, fontWeight: 600 }}>
					<Icon name="favorite" size={12} fill />
					积极情绪
				</div>
			</div>
			<div className="good-main">
				<div style={{ color: "var(--foreground-secondary)", fontSize: 15, lineHeight: 1.5 }}>
					回想今天，写下三件还不错的小事，和它们为什么会发生。
				</div>
				<div className="good-slot good-active">
					<div className="good-check"><Icon name="check" size={16} /></div>
					<div style={{ display: "grid", gap: 5, flex: 1 }}>
						<div style={{ fontSize: 15, fontWeight: 500 }}>和同事一起把方案赶完了</div>
						<div style={{ color: "var(--accent-primary-deep)", fontSize: 13 }}>因为 我们提前列了清单</div>
					</div>
				</div>
				{[2, 3].map((num) => (
					<div className="good-slot" key={num}>
						<div className="good-num">{num}</div>
						<div style={{ display: "grid", gap: 5, flex: 1 }}>
							<div style={{ color: "var(--foreground-muted)", fontSize: 15 }}>写一件还不错的小事…</div>
							<div style={{ color: "var(--foreground-muted)", fontSize: 13 }}>它为什么会发生？（可选）</div>
						</div>
					</div>
				))}
			</div>
		</Screen>
	);
}

function DrawResultPage({ close, start }: { close: () => void; start: () => void }) {
	return (
		<Screen
			onTab={() => undefined}
			className="draw-result-content"
			footer={
				<div style={{ padding: "8px 20px 28px", display: "grid", gap: 10, justifyItems: "center" }}>
					<PrimaryButton onClick={start}>开始这个练习</PrimaryButton>
					<button type="button" onClick={close} style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}>
						再抽一张（今天还剩 1 次）
					</button>
				</div>
			}
		>
			<div className="draw-top">
				<button type="button" onClick={close} aria-label="关闭">
					<Icon name="close" size={24} style={{ color: "var(--foreground-secondary)" }} />
				</button>
				<div className="pp-heading" style={{ fontSize: 16, fontWeight: 600 }}>今日抽卡</div>
				<div style={{ width: 24, height: 24 }} />
			</div>
			<div className="draw-result-hero">
				<div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center", color: "var(--foreground-secondary)", fontSize: 15, fontWeight: 600 }}>
					<Icon name="celebration" size={16} style={{ color: "var(--accent-secondary)" }} />
					你抽到了
				</div>
				<div className="draw-result-card">
					<div className="pp-header-line">
						<div className="pp-chip" style={{ background: "var(--perma-p)", color: "#fff", padding: "5px 11px", fontSize: 11, fontWeight: 600 }}>积极情绪 · P</div>
						<div className="pp-chip" style={{ background: "var(--surface-card)", color: "var(--foreground-secondary)", padding: "5px 11px", fontSize: 11, fontWeight: 600 }}>1 分钟</div>
					</div>
					<Icon name="wb_twilight" size={40} style={{ color: "var(--perma-p)" }} />
					<div className="pp-heading" style={{ fontSize: 25, fontWeight: 600, lineHeight: 1.18 }}>记录今天最好的时刻</div>
					<div style={{ color: "var(--foreground-secondary)", fontSize: 14, lineHeight: 1.5 }}>睡前回想今天，把最好的一刻写下来——一句话就好。</div>
					<div className="pp-chip" style={{ background: "var(--surface-card)", color: "var(--accent-primary-deep)", padding: "5px 11px", fontSize: 11, fontWeight: 600 }}>
						<Icon name="edit" size={13} />
						记录型 · 写一句
					</div>
				</div>
			</div>
		</Screen>
	);
}

function QuizPage({ close, next }: { close: () => void; next: () => void }) {
	return (
		<Screen
			onTab={() => undefined}
			className="quiz-content"
			footer={
				<div style={{ padding: "8px 20px 28px" }}>
					<PrimaryButton onClick={next}>下一步</PrimaryButton>
				</div>
			}
		>
			<div className="quiz-top">
				<button type="button" onClick={close} aria-label="关闭">
					<Icon name="close" size={24} style={{ color: "var(--foreground-secondary)" }} />
				</button>
				<div className="quiz-track"><span /></div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 600 }}>3 / 12</div>
			</div>
			<div className="quiz-main">
				<div style={{ color: "var(--accent-primary-deep)", fontSize: 13, fontWeight: 600 }}>积极思维测评</div>
				<div className="pp-heading" style={{ fontSize: 23, fontWeight: 600, lineHeight: 1.35 }}>
					我能在平凡的一天里，找到值得感激的事。
				</div>
				<div style={{ color: "var(--foreground-muted)", fontSize: 13 }}>凭直觉选，没有对错。</div>
				<div className="quiz-scale">
					{["非常符合", "比较符合", "一般", "不太符合", "很不符合"].map((item) => {
						const active = item === "比较符合";
						return (
							<button type="button" className={cx("quiz-option", active && "pp-card-active")} key={item}>
								<span className={cx("radio-dot", active && "radio-active")}>{active ? <Icon name="check" size={14} /> : null}</span>
								<span style={{ flex: 1, textAlign: "left", color: active ? "var(--accent-primary-deep)" : "var(--foreground-primary)", fontSize: 15, fontWeight: active ? 600 : 500 }}>{item}</span>
							</button>
						);
					})}
				</div>
			</div>
		</Screen>
	);
}

function QuizResultPage({ back, cta }: { back: () => void; cta: () => void }) {
	return (
		<Screen
			onTab={() => undefined}
			className="quiz-result-content"
			footer={
				<div style={{ padding: "6px 16px 24px", display: "grid", gap: 8, justifyItems: "center" }}>
					<PrimaryButton onClick={cta}>去练「意义感」</PrimaryButton>
					<button type="button" onClick={cta} style={{ color: "var(--foreground-muted)", fontSize: 13, fontWeight: 500 }}>回到练习</button>
				</div>
			}
		>
			<BackHeader title="测评结果" onBack={back} />
			<div className="quiz-result-main">
				<div className="score-card">
					<div style={{ display: "flex", alignItems: "baseline", justifyContent: "center" }}>
						<span style={{ color: "#fff", fontSize: 52, fontWeight: 600 }}>76</span>
						<span style={{ color: "#FFFFFFB3", fontSize: 18, fontWeight: 600 }}>/100</span>
					</div>
					<div style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>积极心理水平 · 还不错</div>
					<div style={{ color: "#FFFFFFCC", fontSize: 12, fontWeight: 500 }}>比上次 +4，继续保持</div>
				</div>
				<div style={{ fontSize: 14, fontWeight: 600 }}>五个维度</div>
				<div className="bars">
					{[
						["积极", "82", "var(--perma-p)", 190],
						["投入", "64", "var(--perma-e)", 148],
						["关系", "78", "var(--perma-r)", 181],
						["意义", "58", "var(--perma-m)", 135],
						["成就", "80", "var(--perma-a)", 186],
					].map(([name, score, color, width]) => (
						<div className="bar-row" key={name}>
							<div style={{ width: 34, color: "var(--foreground-secondary)", fontSize: 13, fontWeight: 500 }}>{name}</div>
							<div className="bar-track"><span style={{ width: Number(width), background: color }} /></div>
							<div style={{ width: 24, textAlign: "right", fontSize: 13, fontWeight: 600 }}>{score}</div>
						</div>
					))}
				</div>
				<div className="why-card">
					<Icon name="lightbulb" size={16} fill style={{ color: "var(--accent-primary-deep)" }} />
					<div style={{ flex: 1, color: "var(--foreground-secondary)", fontSize: 13, lineHeight: 1.5 }}>
						你在「关系」「成就」上很有资源；「意义感」可以多练一练。
					</div>
				</div>
			</div>
		</Screen>
	);
}

function ControlCirclePage({ back, done }: { back: () => void; done: () => void }) {
	return (
		<Screen
			onTab={() => undefined}
			className="control-content"
			footer={
				<div style={{ padding: "8px 20px 28px" }}>
					<PrimaryButton onClick={done}>完成，记一笔</PrimaryButton>
				</div>
			}
		>
			<BackHeader title="可控圈" tag="认知 · 3 分钟" onBack={back} />
			<div className="control-main">
				<div style={{ color: "var(--foreground-secondary)", fontSize: 14, lineHeight: 1.5 }}>
					把现在担心的事，分成两栏：你今天能动的，和暂时可以放下的。
				</div>
				<ControlColumn
					title="我能控制的"
					icon="adjust"
					color="var(--accent-primary-deep)"
					bg="var(--accent-primary-soft)"
					items={["先写完方案的开头", "今天早点睡"]}
					add="加一条我能做的"
				/>
				<ControlColumn
					title="暂时放下的"
					icon="cloud"
					color="var(--foreground-secondary)"
					bg="var(--surface-sunken)"
					items={["别人会怎么评价", "结果会不会好"]}
					add="加一条先放下的"
				/>
			</div>
		</Screen>
	);
}

function ControlColumn({
	title,
	icon,
	color,
	bg,
	items,
	add,
}: {
	title: string;
	icon: string;
	color: string;
	bg: string;
	items: string[];
	add: string;
}) {
	return (
		<div className="control-column" style={{ background: bg }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Icon name={icon} size={18} style={{ color }} />
				<div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
			</div>
			{items.map((item, index) => (
				<div className="control-item" key={item}>
					<Icon name={index === 0 && title === "我能控制的" ? "drag_indicator" : title === "我能控制的" ? "drag_indicator" : "remove"} size={15} style={{ color: "var(--foreground-muted)" }} />
					<span>{item}</span>
				</div>
			))}
			<div className="control-add">
				<Icon name="add" size={16} />
				<span>{add}</span>
			</div>
		</div>
	);
}

export function ProductExperience() {
	const [screen, setScreen] = useState<ScreenKey>("today");
	const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
	const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
	const [alternativeOffset, setAlternativeOffset] = useState(0);
	const activeArea = selectedAreas[selectedAreas.length - 1] ?? "stress";
	const activeQuestionGroup = questionGroups[activeArea] ?? questionGroups.stress;
	const recommendation = buildRecommendationFromAnswers(
		selectedAreas,
		selectedQuestion,
		alternativeOffset,
	);

	const goTab = (tab: TabKey) => setScreen(tab === "today" ? "today" : tab);
	const updateSelectedAreas = (areas: string[]) => {
		setSelectedAreas(areas);
		setSelectedQuestion(null);
		setAlternativeOffset(0);
	};
	const goQuestion = () => {
		if (selectedAreas.length === 0) {
			return;
		}
		setSelectedQuestion(null);
		setAlternativeOffset(0);
		setScreen("question");
	};
	const goRecommend = () => {
		if (!selectedQuestion) {
			return;
		}
		setAlternativeOffset(0);
		setScreen("recommend");
	};
	const skipQuestion = () => {
		setSelectedQuestion("说不清，先给我一个轻练习");
		setAlternativeOffset(0);
		setScreen("recommend");
	};
	const startRecommendation = () => {
		if (recommendation.practiceId === "box-breath") {
			setScreen("tutorial");
			return;
		}
		if (recommendation.practiceId === "control-circle") {
			setScreen("controlCircle");
			return;
		}
		setScreen("checkin");
	};
	const back = () => {
		const map: Partial<Record<ScreenKey, ScreenKey>> = {
			question: "today",
			recommend: "question",
			tutorial: "recommend",
			breathing: "tutorial",
			checkin: "recommend",
			record: "checkin",
			library: "practice",
			goodThings: "drawResult",
			drawResult: "practice",
			quiz: "practice",
			quizResult: "quiz",
			controlCircle: "library",
		};
		setScreen(map[screen] ?? "today");
	};

	return (
		<div className="pp-root">
			<style>{css}</style>
			{screen === "today" ? (
				<TodayPage selected={selectedAreas} setSelected={updateSelectedAreas} next={goQuestion} onTab={goTab} />
			) : null}
			{screen === "question" ? (
				<QuestionPage
					selected={selectedQuestion}
					setSelected={setSelectedQuestion}
					next={goRecommend}
					back={back}
					group={activeQuestionGroup}
					skip={skipQuestion}
				/>
			) : null}
			{screen === "recommend" ? (
				<RecommendPage
					recommendation={recommendation}
					start={startRecommendation}
					back={back}
					lighter={() => setAlternativeOffset((value) => value + 1)}
					recordOnly={() => setScreen("record")}
				/>
			) : null}
			{screen === "tutorial" ? <TutorialPage back={back} start={() => setScreen("breathing")} skip={() => setScreen("checkin")} /> : null}
			{screen === "breathing" ? <BreathingPage close={back} done={() => setScreen("checkin")} /> : null}
			{screen === "checkin" ? <CheckinPage recommendation={recommendation} back={back} next={() => setScreen("record")} /> : null}
			{screen === "record" ? <RecordPage recommendation={recommendation} back={back} save={() => setScreen("growth")} /> : null}
			{screen === "practice" ? (
				<PracticePage onTab={goTab} openLibrary={() => setScreen("library")} openDraw={() => setScreen("drawResult")} openQuiz={() => setScreen("quiz")} />
			) : null}
			{screen === "library" ? <LibraryPage back={back} openTutorial={() => setScreen("tutorial")} openControl={() => setScreen("controlCircle")} /> : null}
			{screen === "growth" ? <GrowthPage onTab={goTab} /> : null}
			{screen === "profile" ? <ProfilePage onTab={goTab} /> : null}
			{screen === "goodThings" ? <GoodThingsPage back={back} save={() => setScreen("record")} /> : null}
			{screen === "drawResult" ? <DrawResultPage close={back} start={() => setScreen("goodThings")} /> : null}
			{screen === "quiz" ? <QuizPage close={back} next={() => setScreen("quizResult")} /> : null}
			{screen === "quizResult" ? <QuizResultPage back={back} cta={() => setScreen("practice")} /> : null}
			{screen === "controlCircle" ? <ControlCirclePage back={back} done={() => setScreen("record")} /> : null}
		</div>
	);
}
